<template>
	<div class="charts-wrapper">
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('best_products_revenue')"
			:caption="bestProductName"
			:value="bestProductRevenue"
			:last-period-value="bestProductRevenuePrevious"
			:colors="analyticsStore.bestColors"
			:series="productRevenuesSeries"
			:xaxis="xaxisLabels"
			type="area"
			:formatter="r => currencyStore.readableCurrencyValue(r, false, true, true)"
			:dark="userStore.darkMode"
			:force-y-axis-zero="true"
			:info-button="getString('info_products_not_include')"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('best_products_orders')"
			:caption="bestProductNameUnits"
			:value="bestProductUnits"
			:last-period-value="bestProductUnitsPrevious"
			:colors="analyticsStore.bestColors"
			:series="productUnitsSeries"
			:xaxis="xaxisLabels"
			type="area"
			no-currency
			:dark="userStore.darkMode"
			:force-y-axis-zero="true"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('best_category_revenue')"
			:caption="bestCategoryName"
			:value="bestCategoryRevenue"
			:last-period-value="bestCategoryRevenuePrevious"
			:colors="analyticsStore.bestColors"
			:series="categoryRevenuesSeries"
			:xaxis="xaxisLabels"
			type="area"
			:formatter="r => currencyStore.readableCurrencyValue(r, false, true, true)"
			:dark="userStore.darkMode"
			:force-y-axis-zero="true"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('best_category_orders')"
			:caption="bestCategoryNameUnits"
			:value="bestCategoryUnits"
			:last-period-value="bestCategoryUnitsPrevious"
			:colors="analyticsStore.bestColors"
			:series="categoryUnitsSeries"
			:xaxis="xaxisLabels"
			type="area"
			no-currency
			:dark="userStore.darkMode"
			:force-y-axis-zero="true"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('best_items_products_revenue')"
			:caption="bestVariationName"
			:value="bestVariationRevenue"
			:last-period-value="bestVariationRevenuePrevious"
			:colors="analyticsStore.bestColors"
			:series="variationRevenuesSeries"
			:xaxis="xaxisLabels"
			type="area"
			:formatter="r => currencyStore.readableCurrencyValue(r, false, true, true)"
			:dark="userStore.darkMode"
			:force-y-axis-zero="true"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('best_items_products_orders')"
			:caption="bestVariationNameUnits"
			:value="bestVariationUnits"
			:last-period-value="bestVariationUnitsPrevious"
			:colors="analyticsStore.bestColors"
			:series="variationUnitsSeries"
			:xaxis="xaxisLabels"
			type="area"
			no-currency
			:dark="userStore.darkMode"
			:force-y-axis-zero="true"
		/>
	</div>
</template>

<script>
import AppChart from "@/shared/components/AppChart.vue";
import {useAnalyticsStore, useMenuStore} from "@/console-new/stores";
import {useCurrencyStore, useLanguageStore} from "@/shared/stores";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import {useUserStore} from "@/user/stores";

export default {
	setup() {
		const analyticsStore = useAnalyticsStore();
		const languageStore = useLanguageStore();
		const menuStore = useMenuStore();
		const currencyStore = useCurrencyStore();
		const userStore = useUserStore();
		return {menuStore, analyticsStore, currencyStore, languageStore, getString: languageStore.getString, userStore};
	},
	name: "ProductsChartsAggregated",
	components: {AppChart},
	mixins: [WindowWidth],
	props: {
		dates: {
			type: Object,
			required: true,
			validator: (value) => {
				return value.hasOwnProperty("start") && value.hasOwnProperty("end") && value.hasOwnProperty("previousStart") && value.hasOwnProperty("previousEnd") && value.hasOwnProperty("timeframeIndex");
			}
		},
	},
	computed: {
		selectorsRowHeight() {
			return this.windowWidth > 820 ? 80 : this.windowWidth > 420 ? 150 : 190;
		},
		aggregatedData() {
			return this.analyticsStore.productsAggregated || {};
		},
		// Create Maps for efficient lookups
		categoryMenuMap() {
			const map = new Map();
			if (this.aggregatedData.categories_metadata) {
				this.aggregatedData.categories_metadata.forEach(cat => {
					map.set(cat.menu_category_id, cat.menu_id);
				});
			}
			return map;
		},
		productCategoryMap() {
			const map = new Map();
			if (this.aggregatedData.products_metadata) {
				this.aggregatedData.products_metadata.forEach(product => {
					map.set(product.menu_item_id, product.category_id);
				});
			}
			return map;
		},
		shouldFilterByMenu() {
			return this.analyticsStore.activeMenus && 
				   this.analyticsStore.activeMenus.length > 0 && 
				   this.analyticsStore.activeMenus.length < Object.keys(this.menuStore.menus).length;
		},
		activeMenusSet() {
			return new Set(this.analyticsStore.activeMenus || []);
		},
		xaxisLabels() {
			const xaxisTimes = this.analyticsStore.xaxisTimesAggregated(this.adjustedDates);
			return xaxisTimes.map(time => time.label);
		},
		adjustedDates() {
			if (this.dates.timeframeIndex === 2 || this.dates.timeframeIndex === 6) {
				const now = new Date();
				let adjustedStart, adjustedEnd, adjustedPreviousStart, adjustedPreviousEnd;
				
				if (this.dates.timeframeIndex === 2) {
					adjustedStart = this.analyticsStore.firstDayOfCurrentMonth();
					adjustedEnd = now;
					const elapsedTime = adjustedEnd.getTime() - adjustedStart.getTime();
					adjustedPreviousStart = this.analyticsStore.firstDayOfLastMonth();
					adjustedPreviousEnd = new Date(adjustedPreviousStart.getTime() + elapsedTime);
				} else {
					adjustedStart = this.analyticsStore.firstDayOfCurrentYear();
					adjustedEnd = now;
					const elapsedTime = adjustedEnd.getTime() - adjustedStart.getTime();
					adjustedPreviousStart = this.analyticsStore.firstDayOfLastYear();
					adjustedPreviousEnd = new Date(adjustedPreviousStart.getTime() + elapsedTime);
				}
				
				return {
					...this.dates,
					start: adjustedStart,
					end: adjustedEnd,
					previousStart: adjustedPreviousStart,
					previousEnd: adjustedPreviousEnd
				};
			}
			return this.dates;
		},
		// Product metrics
		bestProductName() {
			const best = this.getBestItem('products', 'gross_sales', false);
			return best ? best.name : '';
		},
		bestProductRevenue() {
			const best = this.getBestItem('products', 'gross_sales', false);
			return best ? best.total : 0;
		},
		bestProductRevenuePrevious() {
			const best = this.getBestItem('products', 'gross_sales', false);
			return best ? this.getItemTotal('products', best.id, 'gross_sales', true) : 0;
		},
		bestProductNameUnits() {
			const best = this.getBestItem('products', 'units_sold', false);
			return best ? best.name : '';
		},
		bestProductUnits() {
			const best = this.getBestItem('products', 'units_sold', false);
			return best ? best.total : 0;
		},
		bestProductUnitsPrevious() {
			const best = this.getBestItem('products', 'units_sold', false);
			return best ? this.getItemTotal('products', best.id, 'units_sold', true) : 0;
		},
		productRevenuesSeries() {
			return this.buildDimensionSeries('products', 'gross_sales', 'item_id');
		},
		productUnitsSeries() {
			return this.buildDimensionSeries('products', 'units_sold', 'item_id');
		},
		// Category metrics
		bestCategoryName() {
			const best = this.getBestItem('categories', 'gross_sales', false);
			return best ? best.name : '';
		},
		bestCategoryRevenue() {
			const best = this.getBestItem('categories', 'gross_sales', false);
			return best ? best.total : 0;
		},
		bestCategoryRevenuePrevious() {
			const best = this.getBestItem('categories', 'gross_sales', false);
			return best ? this.getItemTotal('categories', best.id, 'gross_sales', true) : 0;
		},
		bestCategoryNameUnits() {
			const best = this.getBestItem('categories', 'units_sold', false);
			return best ? best.name : '';
		},
		bestCategoryUnits() {
			const best = this.getBestItem('categories', 'units_sold', false);
			return best ? best.total : 0;
		},
		bestCategoryUnitsPrevious() {
			const best = this.getBestItem('categories', 'units_sold', false);
			return best ? this.getItemTotal('categories', best.id, 'units_sold', true) : 0;
		},
		categoryRevenuesSeries() {
			return this.buildDimensionSeries('categories', 'gross_sales', 'category_id');
		},
		categoryUnitsSeries() {
			return this.buildDimensionSeries('categories', 'units_sold', 'category_id');
		},
		// Variation metrics
		bestVariationName() {
			const best = this.getBestItem('variations', 'gross_sales', false);
			return best ? best.name : '';
		},
		bestVariationRevenue() {
			const best = this.getBestItem('variations', 'gross_sales', false);
			return best ? best.total : 0;
		},
		bestVariationRevenuePrevious() {
			const best = this.getBestItem('variations', 'gross_sales', false);
			return best ? this.getItemTotal('variations', best.id, 'gross_sales', true) : 0;
		},
		bestVariationNameUnits() {
			const best = this.getBestItem('variations', 'units_sold', false);
			return best ? best.name : '';
		},
		bestVariationUnits() {
			const best = this.getBestItem('variations', 'units_sold', false);
			return best ? best.total : 0;
		},
		bestVariationUnitsPrevious() {
			const best = this.getBestItem('variations', 'units_sold', false);
			return best ? this.getItemTotal('variations', best.id, 'units_sold', true) : 0;
		},
		variationRevenuesSeries() {
			return this.buildDimensionSeries('variations', 'gross_sales', 'menu_item_list_product_id');
		},
		variationUnitsSeries() {
			return this.buildDimensionSeries('variations', 'units_sold', 'menu_item_list_product_id');
		},
	},
	methods: {
		calculateTotal(dataKey, field, isPrevious) {
			if (!this.aggregatedData[dataKey]) return 0;
			
			const bucketColumn = this.getBucketColumn(dataKey);
			const startDate = isPrevious ? this.adjustedDates.previousStart : this.adjustedDates.start;
			const endDate = isPrevious ? this.adjustedDates.previousEnd : this.adjustedDates.end;
			
			return this.aggregatedData[dataKey]
				.filter(row => {
					const date = new Date(row[bucketColumn]);
					if (bucketColumn === 'hour_bucket') {
						const adjustedEndDate = new Date(endDate);
						adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
						return date >= startDate && date <= adjustedEndDate;
					}
					return date >= startDate && date <= endDate;
				})
				.reduce((sum, row) => {
					const value = row[field] || 0;
					// Convert cents to actual value for monetary fields
					return sum + (field.includes('total') || field.includes('sales') ? value / 100 : value);
				}, 0);
		},
		buildTimeSeries(dataKey, field, isPrevious) {
			if (!this.aggregatedData[dataKey]) return [];
			
			const bucketColumn = this.getBucketColumn(dataKey);
			const xaxisTimes = this.analyticsStore.xaxisTimesAggregated(this.adjustedDates);
			const startDate = isPrevious ? this.adjustedDates.previousStart : this.adjustedDates.start;
			const endDate = isPrevious ? this.adjustedDates.previousEnd : this.adjustedDates.end;
			
			const series = new Array(xaxisTimes.length).fill(0);
			
			this.aggregatedData[dataKey]
				.filter(row => {
					const date = new Date(row[bucketColumn]);
					if (bucketColumn === 'hour_bucket') {
						const adjustedEndDate = new Date(endDate);
						adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
						return date >= startDate && date <= adjustedEndDate;
					}
					return date >= startDate && date <= endDate;
				})
				.forEach(row => {
					const bucketDate = new Date(row[bucketColumn]);
					
					// Align to hour boundaries for non-hour buckets (like Orders does)
					if (xaxisTimes.length > 0 && bucketColumn !== 'hour_bucket') {
						bucketDate.setHours(xaxisTimes[0].rawDate.getHours(), xaxisTimes[0].rawDate.getMinutes(), 0, 0);
					}
					
					const mockOrder = { date: bucketDate };
					const index = this.analyticsStore.getDatesIndex(this.adjustedDates, xaxisTimes, mockOrder, isPrevious);
					
					if (index >= 0 && index < series.length) {
						const value = Number(row[field] || 0);
						const isMonetaryField = field.includes('total') || field.includes('sales');
						series[index] += isMonetaryField ? value / 100 : value;
					}
				});
			
			return series;
		},
		buildDimensionSeries(dataKey, field, idField) {
			if (!this.aggregatedData[dataKey]) return [];
			
			// Get top items in current period
			const items = this.getTopItems(dataKey, field, false, 6);
			
			const series = items.map((item, index) => ({
				name: item.name,
				color: this.analyticsStore.bestColors[index],
				data: this.buildItemTimeSeries(dataKey, field, idField, item.id, false)
			}));
			
			// Check if all series have only zero values
			const hasData = series.some(s => s.data && s.data.some(v => v > 0));
			if (!hasData) {
				return [];
			}
			
			return series;
		},
		getBestItem(dataKey, field, isPrevious) {
			const items = this.getTopItems(dataKey, field, isPrevious, 1);
			return items.length > 0 ? items[0] : null;
		},
		getTopItems(dataKey, field, isPrevious, limit) {
			if (!this.aggregatedData[dataKey]) return [];
			
			const totals = new Map();
			const names = new Map();
			const idField = this.getIdField(dataKey);
			const bucketColumn = this.getBucketColumn(dataKey);
			const startDate = isPrevious ? this.adjustedDates.previousStart : this.adjustedDates.start;
			const endDate = isPrevious ? this.adjustedDates.previousEnd : this.adjustedDates.end;
			
			this.aggregatedData[dataKey]
				.filter(row => {
					const date = new Date(row[bucketColumn]);
					let dateMatch;
					if (bucketColumn === 'hour_bucket') {
						const adjustedEndDate = new Date(endDate);
						adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
						dateMatch = date >= startDate && date <= adjustedEndDate;
					} else {
						dateMatch = date >= startDate && date <= endDate;
					}
					
					// Apply menu filtering if needed
					if (dateMatch && this.shouldFilterByMenu) {
						const id = row[idField];
						if (dataKey === 'categories') {
							const menuId = this.categoryMenuMap.get(id);
							return menuId && this.activeMenusSet.has(menuId);
						} else if (dataKey === 'products') {
							const categoryId = this.productCategoryMap.get(id);
							const menuId = categoryId ? this.categoryMenuMap.get(categoryId) : null;
							return menuId && this.activeMenusSet.has(menuId);
						} else if (dataKey === 'variations') {
							// For variations, we need to look up through the product metadata
							const variationMeta = this.aggregatedData.variations_metadata?.find(v => v.menu_item_list_product_id === id);
							if (variationMeta && variationMeta.menu_item_id) {
								const categoryId = this.productCategoryMap.get(variationMeta.menu_item_id);
								const menuId = categoryId ? this.categoryMenuMap.get(categoryId) : null;
								return menuId && this.activeMenusSet.has(menuId);
							}
							return false;
						}
					}
					
					return dateMatch;
				})
				.forEach(row => {
					const id = row[idField];
					if (id !== null && id !== undefined) {
						const value = row[field] || 0;
						// Convert cents to actual value for monetary fields
						const actualValue = field.includes('total') || field.includes('sales') ? value / 100 : value;
						totals.set(id, (totals.get(id) || 0) + actualValue);
						
						// Store name from metadata
						if (!names.has(id)) {
							const name = this.getItemName(dataKey, id);
							names.set(id, name);
						}
					}
				});
			
			return Array.from(totals.entries())
				.map(([id, total]) => ({ id, total, name: names.get(id) || '' }))
				.sort((a, b) => b.total - a.total)
				.slice(0, limit);
		},
		getItemTotal(dataKey, itemId, field, isPrevious) {
			if (!this.aggregatedData[dataKey]) return 0;
			
			const idField = this.getIdField(dataKey);
			const bucketColumn = this.getBucketColumn(dataKey);
			const startDate = isPrevious ? this.adjustedDates.previousStart : this.adjustedDates.start;
			const endDate = isPrevious ? this.adjustedDates.previousEnd : this.adjustedDates.end;
			
			return this.aggregatedData[dataKey]
				.filter(row => {
					const date = new Date(row[bucketColumn]);
					let dateMatch;
					if (bucketColumn === 'hour_bucket') {
						const adjustedEndDate = new Date(endDate);
						adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
						dateMatch = date >= startDate && date <= adjustedEndDate;
					} else {
						dateMatch = date >= startDate && date <= endDate;
					}
					return dateMatch && row[idField] == itemId;
				})
				.reduce((sum, row) => {
					const value = row[field] || 0;
					// Convert cents to actual value for monetary fields
					return sum + (field.includes('total') || field.includes('sales') ? value / 100 : value);
				}, 0);
		},
		buildItemTimeSeries(dataKey, field, idField, itemId, isPrevious) {
			if (!this.aggregatedData[dataKey]) return [];
			
			const bucketColumn = this.getBucketColumn(dataKey);
			const xaxisTimes = this.analyticsStore.xaxisTimesAggregated(this.adjustedDates);
			const startDate = isPrevious ? this.adjustedDates.previousStart : this.adjustedDates.start;
			const endDate = isPrevious ? this.adjustedDates.previousEnd : this.adjustedDates.end;
			
			const series = new Array(xaxisTimes.length).fill(0);
			
			this.aggregatedData[dataKey]
				.filter(row => {
					const date = new Date(row[bucketColumn]);
					let dateMatch;
					if (bucketColumn === 'hour_bucket') {
						const adjustedEndDate = new Date(endDate);
						adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
						dateMatch = date >= startDate && date <= adjustedEndDate;
					} else {
						dateMatch = date >= startDate && date <= endDate;
					}
					return dateMatch && row[idField] == itemId;
				})
				.forEach(row => {
					const bucketDate = new Date(row[bucketColumn]);
					
					// Align to hour boundaries for non-hour buckets
					if (xaxisTimes.length > 0 && bucketColumn !== 'hour_bucket') {
						bucketDate.setHours(xaxisTimes[0].rawDate.getHours(), xaxisTimes[0].rawDate.getMinutes(), 0, 0);
					}
					
					const mockOrder = { date: bucketDate };
					const index = this.analyticsStore.getDatesIndex(this.adjustedDates, xaxisTimes, mockOrder, isPrevious);
					
					if (index >= 0 && index < series.length) {
						const value = Number(row[field] || 0);
						const isMonetaryField = field.includes('total') || field.includes('sales');
						series[index] += isMonetaryField ? value / 100 : value;
					}
				});
			
			return series;
		},
		getBucketColumn(dataKey) {
			// Use the bucket column from the aggregated data response
			return this.aggregatedData.bucketColumn || 'day_bucket';
		},
		getIdField(dataKey) {
			switch(dataKey) {
				case 'products': return 'item_id';
				case 'categories': return 'category_id';
				case 'variations': return 'menu_item_list_product_id';
				default: return 'item_id';
			}
		},
		getItemName(dataKey, itemId) {
			// Look up item name from metadata
			switch(dataKey) {
				case 'products':
					const product = this.aggregatedData.products_metadata?.find(p => p.menu_item_id == itemId);
					return product?.name || `Product ${itemId}`;
				case 'categories':
					const category = this.aggregatedData.categories_metadata?.find(c => c.menu_category_id == itemId);
					return category?.name || `Category ${itemId}`;
				case 'variations':
					const variation = this.aggregatedData.variations_metadata?.find(v => v.menu_item_list_product_id == itemId);
					return variation?.product_name || `Variation ${itemId}`;
				default:
					return `Item ${itemId}`;
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.charts-wrapper {
	display: flex;
	justify-content: space-between;
	flex-wrap: wrap;
	width: 100%;
	margin-bottom: calc(-1 * var(--main-padding));

	.chart-container {
		min-width: calc((100% - var(--main-padding)) / 2);
		max-width: calc((100% - var(--main-padding)) / 2);
		margin-bottom: var(--main-padding);

		&:deep(.chart-svg-container) {
			transition: min-height .35s ease-in-out;
			height: 200px;
		}

		&.expanded {
			min-width: 100%;
			max-width: 100%;

			&:deep(.chart-svg-container) {
				height: 350px;
			}
		}


		&:deep(.card-header h3) {
			font-size: 18px;
		}

	}

	@media (max-width: 920px) {
		justify-content: space-between;
		.chart-container {
			max-width: calc((100% - var(--main-padding)) / 2);

			&.expanded {
				max-width: 100%;
			}
		}
	}

	@media (max-width: 670px) {
		justify-content: center;
		.chart-container {
			min-width: unset;
			max-width: 100%;
			width: 100%;

			&.expanded {
				max-width: 100%;
			}
		}
	}
}
</style>